import React, { useCallback, useEffect, useRef, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { Tag } from '@carbon/react';
import { CheckmarkFilled, ChevronLeft, ChevronRight } from '@carbon/icons-react';

const COLOR_PAIRS = [
  { primary: '#0072c3', secondary: '#00539a' },
  { primary: '#007d79', secondary: '#005d5d' },
  { primary: '#6929c4', secondary: '#491d8b' },
  { primary: '#1192e8', secondary: '#0072c3' },
  { primary: '#009d9a', secondary: '#007d79' },
  { primary: '#0043ce', secondary: '#002d9c' },
  { primary: '#198038', secondary: '#0e6027' },
  { primary: '#8a3ffc', secondary: '#6929c4' },
  { primary: '#0f62fe', secondary: '#0043ce' },
];

const LandingShowcaseSlider = ({
  rows,
  colorOffset = 0,
  mediaFirstWhenEven = false,
  testIdPrefix = 'showcase',
  autoPlayMs = 6000,
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, duration: 28 });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const pausedRef = useRef(false);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((index) => emblaApi?.scrollTo(index), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return undefined;

    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    emblaApi.on('select', onSelect);
    onSelect();

    return () => emblaApi.off('select', onSelect);
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi || autoPlayMs <= 0) return undefined;

    const tick = () => {
      if (pausedRef.current) return;
      emblaApi.scrollNext();
    };

    const interval = setInterval(tick, autoPlayMs);
    return () => clearInterval(interval);
  }, [emblaApi, autoPlayMs]);

  const renderSlide = (row, index) => {
    const mediaFirst = mediaFirstWhenEven ? index % 2 === 0 : index % 2 !== 0;
    const colors = COLOR_PAIRS[(index + colorOffset) % COLOR_PAIRS.length];

    const textPanel = (
      <div
        className="showcase-split-panel"
        style={{ backgroundColor: colors.primary }}
        data-testid={`${testIdPrefix}-text-${index}`}
      >
        <div className="showcase-split-panel__tag-wrap">
          <Tag type={row.tagType}>{row.tag}</Tag>
        </div>
        <h3 className="showcase-split-panel__title">{row.title}</h3>
        <p className="showcase-split-panel__desc">{row.description}</p>
        <ul className="showcase-split-panel__bullets">
          {row.bullets.map((bullet) => (
            <li key={bullet}>
              <CheckmarkFilled size={20} />
              <span>{bullet}</span>
            </li>
          ))}
        </ul>
      </div>
    );

    const mediaPanel = (
      <div
        className="showcase-split-panel showcase-split-panel--media"
        style={{ backgroundColor: colors.secondary }}
        data-testid={`${testIdPrefix}-media-${index}`}
      >
        <img src={row.image} alt={row.alt} loading="lazy" />
      </div>
    );

    return (
      <div
        className="showcase-slider__slide"
        key={row.title}
        data-testid={`${testIdPrefix}-row-${index}`}
      >
        <div className="showcase-split-row">
          {mediaFirst ? (
            <>
              {mediaPanel}
              {textPanel}
            </>
          ) : (
            <>
              {textPanel}
              {mediaPanel}
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      className="showcase-slider"
      data-testid={`${testIdPrefix}-slider`}
      onMouseEnter={() => { pausedRef.current = true; }}
      onMouseLeave={() => { pausedRef.current = false; }}
      onFocusCapture={() => { pausedRef.current = true; }}
      onBlurCapture={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) pausedRef.current = false;
      }}
    >
      <div className="showcase-slider__viewport" ref={emblaRef}>
        <div className="showcase-slider__container">
          {rows.map((row, index) => renderSlide(row, index))}
        </div>
      </div>

      <div className="showcase-slider__controls">
        <button
          type="button"
          className="showcase-slider__nav-btn"
          onClick={scrollPrev}
          aria-label="Previous slide"
          data-testid={`${testIdPrefix}-prev`}
        >
          <ChevronLeft size={24} />
        </button>

        <div className="showcase-slider__dots" role="tablist" aria-label="Showcase slides">
          {rows.map((row, index) => (
            <button
              key={row.title}
              type="button"
              role="tab"
              aria-selected={selectedIndex === index}
              aria-label={`Go to slide ${index + 1}: ${row.title}`}
              className={`showcase-slider__dot${selectedIndex === index ? ' showcase-slider__dot--active' : ''}`}
              onClick={() => scrollTo(index)}
              data-testid={`${testIdPrefix}-dot-${index}`}
            />
          ))}
        </div>

        <button
          type="button"
          className="showcase-slider__nav-btn"
          onClick={scrollNext}
          aria-label="Next slide"
          data-testid={`${testIdPrefix}-next`}
        >
          <ChevronRight size={24} />
        </button>
      </div>
    </div>
  );
};

export default LandingShowcaseSlider;
